
import * as React from 'react';
import { Helmet } from 'react-helmet-async';
import { useEffect, useState } from 'react';

// @mui
import {
  Box,
  Stack,
  Container,
  Typography,
  FormControl,
  Select,
  MenuItem,
  InputLabel,
  FormHelperText,
  CircularProgress,
  Paper,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  Grid,
  Divider,
  Chip,
  Avatar,
  Tooltip
} from '@mui/material';
import {
  Timeline,
  TimelineItem,
  TimelineSeparator,
  TimelineConnector,
  TimelineContent,
  TimelineDot,
} from '@mui/lab';
import dayjs from "dayjs";
import { DataGrid, GridToolbarContainer, GridToolbarExport } from '@mui/x-data-grid';
import { styled } from '@mui/system';
import { useSnackbar } from '../components/snackbar';
import { getExisitingPlanUsers, useGetUserSubscriptionLogs } from 'api/users';
import useResponsive from '../hooks/useResponsive';
import { useAuthDispatch } from 'components/AuthStateProvider';

const CustomToolbar = () => {
  return (
    <GridToolbarContainer>
      <GridToolbarExport />
    </GridToolbarContainer>
  );
};
//-----------------------------------------------------------------------

export default function PlanUsers() {
  const { enqueueSnackbar } = useSnackbar();
  const isLg = useResponsive('up', 'md');
  const { account } = useAuthDispatch();

  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filteredData, setFilteredData] = useState([]);
  const [subscriptionType, setSubscriptionType] = useState('');
  const [subscriptionStatus, setSubscriptionStatus] = useState('');
  const [selectedRow, setSelectedRow] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [planName, setPlanName] = useState('')
  const [sortOrder, setSortOrder] = useState('desc');
  const { userProfileData, isLoading: profileLoading } =
    useGetUserSubscriptionLogs(
      isDialogOpen && selectedRow?.phone_number
        ? { phone_number: selectedRow.phone_number }
        : null
    );


  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async (status = '', type = '', planName, sort = 'desc') => {
    try {
      setLoading(true)
      status = status === 'None' ? '' : status
      type = type === 'None' ? '' : type
      planName = planName === 'None' ? '' : planName
      const response = await getExisitingPlanUsers({ status, type, planName, sort });
      setLoading(false);
      if (response?.success) {
        setData(response?.data || []);
        setFilteredData(response?.data || []);
      } else {
        enqueueSnackbar(response?.message, { variant: 'error' });
      }
    } catch (e) {
      enqueueSnackbar("Unable to fetch B2B users.", { variant: 'error' });
    }
  };

  const handlePlanNameChange = (event) => {
    const value = event.target.value;
    setPlanName(value);
    fetchData(subscriptionStatus, subscriptionType, value);
  };
  const handleSubTypeChange = (event) => {
    const value = event.target.value;
    setSubscriptionType(value);
    fetchData(subscriptionStatus, value, planName);
  };


  const handleStatusChange = (event) => {
    const value = event.target.value;
    setSubscriptionStatus(value);
    fetchData(value, subscriptionType, planName);
  };

  const handleRowClick = (params) => {
    const clickedRowId = params.row.rowId;
    const fullRowData = filteredData.find((row) => row.id === clickedRowId);
    setSelectedRow(fullRowData);
    setIsDialogOpen(true);
  };

  const handleCloseDialog = () => {
    setIsDialogOpen(false);
    setSelectedRow(null);
  };


  const columns = [
    { field: 'id', headerName: 'ID', width: 70 },
    { field: 'plan_name', headerName: 'Plan Name', width: 150 },
    { field: 'phone_number', headerName: 'Phone Number', width: 150 },
    { field: 'created_at', headerName: 'Created At', width: 200 },
    { field: 'expiry_date', headerName: 'Expiry Date', width: 200 },
    { field: 'subscription_type', headerName: 'Subscription Type', width: 150 },
    {
      field: 'status',
      headerName: 'Status',
      width: 120,
      renderCell: (params) => {
        return (
          <Typography color={params.row.status === 'active' || params.row.status === 'created' ? 'green' : 'red'}>
            {params.row.status} {/* This uses the status field directly */}
          </Typography>
        );
      },
    },
    {
      field: 'payment_id', // Your extra field
      headerName: 'Payment Id',
      width: 150,
      hide: true, // Hide this field in the UI
    },
  ];

  const rows = filteredData.map((row, index) => ({
    id: index + 1,
    plan_name: row?.plan_name,
    phone_number: row?.phone_number,
    created_at: dayjs(row?.created_at).format('DD/MM/YYYY HH:mm:ss'),
    expiry_date: dayjs(row?.expiry_date).format('DD/MM/YYYY HH:mm:ss'),
    subscription_type: row?.subscription_type,
    last_payment_id: row?.last_payment?.id,
    status: row?.subscription_status,
    rowId: row?.id,
    payment_id: row?.last_payment?.id ?? 'N/A',

  }));

  return (
    <>
      <Helmet>
        <title> Active Users</title>
      </Helmet>

      <Container sx={{ marginTop: isLg ? 0 : 1 }}>
        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          mb={2}
        >
          {/* LEFT - TITLE */}
          <Typography
            variant="h4"
            sx={{ whiteSpace: 'nowrap' }}
          >
            Subscribed Users
          </Typography>

          {/* RIGHT - FILTERS */}
          <Stack direction="row" spacing={2} alignItems="center">

            <FormControl size="small" sx={{ width: 180 }}>
              <InputLabel>Sort</InputLabel>
              <Select
                value={sortOrder}
                label="Sort"
                onChange={(e) => {
                  const value = e.target.value;
                  setSortOrder(value);
                  fetchData(subscriptionStatus, subscriptionType, planName, value);
                }}
              >
                <MenuItem value="desc">Ending Soon</MenuItem>
                <MenuItem value="asc">Ending Later</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Plan</InputLabel>
              <Select value={planName} label="Plan" onChange={handlePlanNameChange}>
                <MenuItem value="Basic Plan">Basic</MenuItem>
                <MenuItem value="Premium Plan">Premium</MenuItem>
                <MenuItem value="Clinical Plan">Clinical</MenuItem>
                <MenuItem value="Clinical Lite Plan">Lite</MenuItem>
                <MenuItem value="None">All</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 150 }}>
              <InputLabel>Type</InputLabel>
              <Select value={subscriptionType} label="Type" onChange={handleSubTypeChange}>
                <MenuItem value="trial">Trial</MenuItem>
                <MenuItem value="paid">Paid</MenuItem>
                <MenuItem value="None">All</MenuItem>
              </Select>
            </FormControl>

            <FormControl size="small" sx={{ width: 170 }}>
              <InputLabel>Status</InputLabel>
              <Select value={subscriptionStatus} label="Status" onChange={handleStatusChange}>
                <MenuItem value="active">Active</MenuItem>
                <MenuItem value="expired">Expired</MenuItem>
                <MenuItem value="created">Created</MenuItem>
                <MenuItem value="cancel">Cancelled</MenuItem>
                <MenuItem value="None">All</MenuItem>
              </Select>
            </FormControl>

          </Stack>
        </Stack>

        <Paper sx={{ height: 550, width: '100%' }}>
          {loading ? (
            <Box sx={{ display: 'flex', justifyContent: 'center', marginTop: '30px', alignItems: 'center' }}>
              <CircularProgress />
            </Box>
          ) : (
            <DataGrid
              rows={rows}
              columns={columns}
              getRowClassName={(params) =>
                params.row.last_payment_id ? 'highlighted-row' : ''
              }

              sx={{
                '& .MuiDataGrid-row.highlighted-row': {
                  backgroundColor: '#e0f7fa !important', // Apply color to rows with last_payment_id
                },

              }}
              initialState={{
                pagination: {
                  paginationModel: {
                    pageSize: 50,
                  },
                },
              }}
              components={{
                ...(account?.userType !== 'marketingteam' && {
                  Toolbar: CustomToolbar,
                }),
              }}

              pageSizeOptions={[50]}
              checkboxSelection={account?.userType !== 'marketingteam'}
              disableRowSelectionOnClick
              onRowClick={handleRowClick}

            />
          )}
        </Paper>


        {/* <Dialog open={isDialogOpen} onClose={handleCloseDialog}>
          <DialogTitle>Payment Details</DialogTitle>
          <DialogContent>
            {selectedRow && (
              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1 }}>
                <Typography> <strong>Payment ID:</strong> {selectedRow?.last_payment?.id ?? 'N/A'}</Typography>
                <Typography><strong>Amount:</strong> {((selectedRow?.last_payment?.amount) / 100) ?? 'N/A'}</Typography>
                <Typography><strong>Time:</strong> {selectedRow?.last_paid_at ? dayjs(selectedRow?.last_paid_at).format('DD/MM/YYYY HH:mm:ss') : 'N/A'}</Typography>
                <Typography><strong>Method of Payment: </strong>{selectedRow?.last_payment?.method ?? 'N/A'}</Typography>
                <Typography><strong>Status:</strong> {selectedRow?.last_payment?.status ?? 'N/A'}</Typography>
                <Typography><strong>User Email:</strong> {selectedRow?.last_payment?.email ?? 'N/A'}</Typography>
              </Box>
            )}
          </DialogContent>
          <DialogActions>
            <Button onClick={handleCloseDialog}>Close</Button>
          </DialogActions>
        </Dialog> */}
        <Dialog open={isDialogOpen} onClose={handleCloseDialog} maxWidth="md" fullWidth>
          <DialogTitle>User Profile</DialogTitle>

          <DialogContent>
            {profileLoading ? (
              <Box sx={{ display: 'flex', justifyContent: 'center', p: 3 }}>
                <CircularProgress />
              </Box>
            ) : userProfileData ? (
              <Grid container spacing={7}>

                {/* 🔹 LEFT SIDE */}
                <Grid item xs={12} md={6}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', gap: 3 }}>

                    {/* Profile */}
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                      <Avatar
                        sx={{
                          width: 60,
                          height: 60,
                          bgcolor: 'primary.main',
                          fontSize: 24,
                          fontWeight: 'bold'
                        }}
                      >
                        {userProfileData?.profile?.name?.charAt(0)?.toUpperCase() || 'U'}
                      </Avatar>
                      <Box>
                        <Typography variant="h6">
                          {userProfileData?.profile?.phone_number || 'N/A'}
                        </Typography>

                        <Typography variant="body2" color="text.secondary">
                          User Name: {userProfileData?.profile?.name || 'N/A'} ({userProfileData?.profile?.gender || 'N/A'})
                        </Typography>
                        <Typography variant="body2" color="text.secondary">
                          Email ID: {userProfileData?.profile?.email || 'N/A'}
                        </Typography>

                      </Box>
                    </Box>

                    <Divider />

                    {/* Stats (optional if backend adds later) */}
                    {userProfileData?.stats && (
                      <>
                        <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                          <Box>
                            <Typography variant="subtitle2">ECG This Month</Typography>
                            <Typography variant="h6">
                              {userProfileData?.stats?.ecg_this_month || 0}
                            </Typography>
                          </Box>
                          <Box>
                            <Typography variant="subtitle2">Total Tests</Typography>
                            <Typography variant="h6">
                              {userProfileData?.stats?.total_tests || 0}
                            </Typography>
                          </Box>
                        </Box>

                        <Divider />
                      </>
                    )}

                    {/* Current Plan */}
                    <Box sx={{ p: 2, border: '1px solid #eee', borderRadius: 2 }}>
                      <Typography fontWeight="bold">Current Plan</Typography>

                      <Typography>
                        {userProfileData?.current_plan?.plan_name || 'N/A'}
                      </Typography>

                      <Typography variant="body2">
                        Purchased: {userProfileData?.current_plan?.purchase_date || 'N/A'}
                      </Typography>

                      <Typography variant="body2">
                        Expiry: {userProfileData?.current_plan?.expiry_date || 'N/A'}
                      </Typography>

                      <Typography
                        variant="body2"
                        color={
                          userProfileData?.current_plan?.status === 'active'
                            ? 'green'
                            : 'red'
                        }
                      >
                        Status: {userProfileData?.current_plan?.status || 'N/A'}
                      </Typography>
                    </Box>

                  </Box>
                </Grid>

                <Grid item xs={12} md={6}>
                  <Box>
                    <Typography fontWeight="bold" mb={2}>
                      Plan History
                    </Typography>

                    <Box
                      sx={{
                        maxHeight: 400, // adjust as needed
                        overflowY: 'auto',
                        pr: 1,
                      }}
                    >
                      <Timeline
                        sx={{
                          p: 0,
                          m: 0,
                          [`& .MuiTimelineItem-root:before`]: {
                            flex: 0,
                            padding: 0,
                          },
                        }}
                      >
                        <Box
                          sx={{
                            display: 'flex',
                            justifyContent: 'space-between',
                            alignItems: 'center',
                            mb: 2
                          }}
                        >
                          <Typography variant="body2" fontWeight="bold">
                            Total Tests (All Plans):
                          </Typography>

                          <Typography variant="body2" fontWeight="bold">
                            {userProfileData?.total_tests_all_plans || 0}
                          </Typography>
                        </Box>
                        {userProfileData?.plan_history?.map((plan, index) => (
                          <TimelineItem key={index}>
                            <TimelineSeparator>
                              <TimelineDot
                                color={plan.status === 'active' ? 'primary' : 'grey'}
                              />
                              {index !== userProfileData.plan_history.length - 1 && (
                                <TimelineConnector />
                              )}
                            </TimelineSeparator>

                            <TimelineContent>
                              <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.8 }}>

                                {/* Row 1: Plan Name */}
                                <Box
                                  sx={{
                                    display: 'flex',
                                    justifyContent: 'space-between',
                                    alignItems: 'center',
                                    gap: 2,
                                    flexWrap: 'wrap'
                                  }}
                                >
                                  <Typography
                                    fontWeight={plan.status === 'active' ? 'bold' : 'normal'}
                                  >
                                    {plan.plan_name}
                                    {plan.status === 'active' && ' (Active)'}
                                  </Typography>

                                  <Tooltip
                                    title={`Total Tests: ${plan?.total_tests || 0} | Total Interpretations: ${plan?.interpretation_count || 0}`}
                                    arrow
                                  >
                                    <Chip
                                      label={`TT: ${plan?.total_tests || 0} | INT: ${plan?.interpretation_count || 0}`}
                                      size="small"
                                      color="primary"
                                      variant="outlined"
                                    />
                                  </Tooltip>
                                </Box>

                                {/* Row 3: Dates */}
                                <Typography variant="body2" color="text.secondary">
                                  {plan.start_date_formatted} - {plan.expiry_date_formatted}
                                </Typography>

                                {/* Row 4: Type */}
                                <Typography variant="body2" color="text.secondary">
                                  Type: {plan.subscription_type}
                                </Typography>

                              </Box>
                            </TimelineContent>
                          </TimelineItem>
                        ))}
                      </Timeline>
                    </Box>
                  </Box>
                </Grid>

              </Grid>
            ) : (
              <Typography>No Data Found</Typography>
            )}
          </DialogContent>

          <DialogActions>
            <Button onClick={handleCloseDialog} variant="contained">
              Close
            </Button>
          </DialogActions>
        </Dialog>


      </Container>
    </>
  );
}


